import { dateToString, stringToDate } from 'src/app/core/util/date-converter';

/**
 * An Inbox filter, built the way Bills to Pay builds one: a list of conditions,
 * each a field, an operator and a value.
 *
 * The Inbox filters on the server (Search/searchInvoices), whose parameters are
 * lists — suppliers=, categories=, tags=, … — ANDed across fields and ORed
 * within one. So conditions on different fields all have to match, and a second
 * condition on the same field widens it ("supplier is A, or supplier is B").
 * The panel says so on the row rather than pretending otherwise.
 *
 * The `searchCriteria` string is the persisted form. It carries the flat
 * parameters the search endpoint and older saved filters use, plus `cond=`
 * parameters so the panel can rebuild exactly what was entered.
 */

export type InboxConditionField =
  | 'state'
  | 'supplier'
  | 'category'
  | 'tag'
  | 'job'
  | 'job2'
  | 'submitter'
  | 'assignee'
  | 'date'
  | 'amount';

export type InboxConditionType = 'enum' | 'date' | 'number';

export interface InboxConditionOption {
  value: string;
  label: string;
}

export interface InboxFilterCondition {
  field: InboxConditionField;
  op: string;
  value?: string;
  /**
   * A list field's choices ("supplier is A or B"). Only the panel holds them this way: the
   * criteria string still carries one `cond=` per value, as it always has, so everything
   * that reads a saved filter is unchanged.
   */
  values?: string[];
  /** High end of a `between`. */
  valueTo?: string;
  /** False while the row is still open for editing. */
  confirmed?: boolean;
}

export interface InboxConditionFieldDef {
  value: InboxConditionField;
  label: string;
  type: InboxConditionType;
  /** The search endpoint's list parameter for an enum field. */
  param?: string;
}

// Field keys never contain the name of a search parameter ("assignee", not
// "assignedTo"): the page reads criteria by substring, and a `cond=` segment
// must not be mistaken for one of the flat parameters.
export const INBOX_CONDITION_FIELDS: InboxConditionFieldDef[] = [
  // Status is not a search parameter: it picks which list the search runs
  // over (For Review / Processed / both), exactly as the title picker does.
  { value: 'state', label: 'Status', type: 'enum' },
  { value: 'supplier', label: 'Supplier', type: 'enum', param: 'suppliers' },
  { value: 'category', label: 'Category', type: 'enum', param: 'categories' },
  { value: 'tag', label: 'Tag', type: 'enum', param: 'tags' },
  { value: 'job', label: 'Job', type: 'enum', param: 'track1' },
  { value: 'job2', label: 'Job', type: 'enum', param: 'track2' },
  { value: 'submitter', label: 'Submitter', type: 'enum', param: 'submitters' },
  { value: 'assignee', label: 'Assigned to', type: 'enum', param: 'assignedTo' },
  { value: 'date', label: 'Date', type: 'date' },
  { value: 'amount', label: 'Amount', type: 'number' }
];

/** The search endpoint matches values; it has no "is not". */
const OPS_BY_TYPE: { [type: string]: InboxConditionOption[] } = {
  enum: [
    { value: 'is', label: 'is' }
  ],
  date: [
    { value: 'on', label: 'is on' },
    { value: 'before', label: 'is before' },
    { value: 'after', label: 'is after' },
    { value: 'between', label: 'is between' }
  ],
  number: [
    { value: 'eq', label: 'equals' },
    { value: 'gte', label: 'is at least' },
    { value: 'lte', label: 'is at most' },
    { value: 'between', label: 'is between' }
  ]
};

export const INBOX_RANGE_OPS = ['between'];

/** The Status field's values. Two Status conditions ("For Review or Processed") mean both. */
export const INBOX_STATUS_OPTIONS: InboxConditionOption[] = [
  { value: 'for-review', label: 'For Review' },
  { value: 'processed', label: 'Processed' }
];

/** Which statuses a criteria string asks for; null when it has no Status condition. */
export function statusesFromCriteria(criteria: string): { forReview: boolean; processed: boolean } | null {
  const values = inboxCriteriaToConditions(criteria).filter(c => c.field === 'state').map(c => c.value);
  if (!values.length) return null;
  return { forReview: values.includes('for-review'), processed: values.includes('processed') };
}

/** Written for "has no tag" / "has no job", which the search endpoint reads. */
export const UNASSIGNED_TAG = 'Unassigned tags';
export const UNASSIGNED_TRACKING = 'Unassigned tracking';

export function inboxFieldDef(field: InboxConditionField): InboxConditionFieldDef {
  return INBOX_CONDITION_FIELDS.find(f => f.value === field);
}

export function inboxFieldType(field: InboxConditionField): InboxConditionType {
  return inboxFieldDef(field)?.type || 'enum';
}

export function inboxOpsFor(field: InboxConditionField): InboxConditionOption[] {
  return OPS_BY_TYPE[inboxFieldType(field)] || [];
}

/** A list condition's choices, whether it holds them as `values` or as the one `value`. */
export function inboxConditionValues(condition: InboxFilterCondition): string[] {
  if (!condition) return [];
  if (condition.values?.length) return condition.values;
  return `${condition.value ?? ''}` !== '' ? [condition.value] : [];
}

/** One condition per value: the shape the criteria string and the search take. */
export function expandInboxConditions(conditions: InboxFilterCondition[]): InboxFilterCondition[] {
  const out: InboxFilterCondition[] = [];
  (conditions || []).filter(Boolean).forEach(c => {
    if (inboxFieldType(c.field) !== 'enum') { out.push(c); return; }
    inboxConditionValues(c).forEach(v => out.push({ ...c, value: v, values: undefined }));
  });
  return out;
}

/** The reverse, for the panel: a field's conditions folded into one with several values. */
export function mergeInboxConditions(conditions: InboxFilterCondition[]): InboxFilterCondition[] {
  const out: InboxFilterCondition[] = [];
  (conditions || []).filter(Boolean).forEach(c => {
    if (inboxFieldType(c.field) !== 'enum') { out.push(c); return; }
    const values = inboxConditionValues(c);
    const first = out.find(o => o.field === c.field && o.op === c.op && !!o.confirmed === !!c.confirmed);
    if (first) {
      first.values = first.values.concat(values.filter(v => !first.values.includes(v)));
    } else {
      out.push({ ...c, values: values.slice(), value: values[0] || '' });
    }
  });
  return out;
}

/** "A", "A or B", "A, B or C". */
function joinOr(labels: string[]): string {
  if (labels.length <= 1) return labels[0] || '';
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

export function isInboxConditionComplete(condition: InboxFilterCondition): boolean {
  if (!condition) return false;
  if (inboxFieldType(condition.field) === 'enum') {
    return inboxConditionValues(condition).length > 0;
  }
  if (INBOX_RANGE_OPS.includes(condition.op)) {
    return `${condition.value ?? ''}` !== '' && `${condition.valueTo ?? ''}` !== '';
  }
  return `${condition.value ?? ''}` !== '';
}

// ── Reading back ────────────────────────────────────────────────────────────

/** Display names for fields whose label depends on the organisation. */
export interface InboxFieldLabels {
  job?: string;
  job2?: string;
}

export function inboxFieldLabel(field: InboxConditionField, labels: InboxFieldLabels = {}): string {
  if (field === 'job' && labels.job) return labels.job;
  if (field === 'job2' && labels.job2) return labels.job2;
  return inboxFieldDef(field)?.label || field;
}

function valueLabel(condition: InboxFilterCondition): string {
  if (condition.value === UNASSIGNED_TAG || condition.value === UNASSIGNED_TRACKING) {
    return 'unassigned';
  }
  if (condition.field === 'state') {
    return INBOX_STATUS_OPTIONS.find(o => o.value === condition.value)?.label || condition.value;
  }
  if (inboxFieldType(condition.field) === 'number') {
    return formatAmount(condition.value);
  }
  if (inboxFieldType(condition.field) === 'date') {
    return formatDate(condition.value);
  }
  return condition.value;
}

function formatAmount(value: string): string {
  const number = Number(value);
  return isNaN(number) ? value : number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(value: string): string {
  const date = stringToDate(value);
  return date && !isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : value;
}

/** One condition as a phrase, e.g. `supplier is Bunnings`. */
export function describeInboxCondition(condition: InboxFilterCondition, labels: InboxFieldLabels = {}): string {
  if (!condition) return '';

  const field = inboxFieldLabel(condition.field, labels).toLowerCase();
  const op = inboxOpsFor(condition.field).find(o => o.value === condition.op)?.label || condition.op;

  if (INBOX_RANGE_OPS.includes(condition.op)) {
    const to = { ...condition, value: condition.valueTo };
    return `${field} ${op} ${valueLabel(condition) || '?'} and ${valueLabel(to) || '?'}`;
  }

  if (inboxFieldType(condition.field) === 'enum') {
    return `${field} ${op} ${joinOr(inboxConditionValues(condition).map(v => valueLabel({ ...condition, value: v })))}`;
  }

  return `${field} ${op} ${valueLabel(condition)}`;
}

/**
 * The whole rule as one line — the search-field chip and the saved-filter list
 * read it. Same-field conditions are joined with "or", as the search runs them.
 */
export function describeInboxConditions(conditions: InboxFilterCondition[], labels: InboxFieldLabels = {}): string {
  const byField = new Map<string, string[]>();

  (conditions || []).filter(Boolean).forEach(c => {
    const phrases = byField.get(c.field) || [];
    phrases.push(describeInboxCondition(c, labels));
    byField.set(c.field, phrases);
  });

  return Array.from(byField.values()).map(phrases => phrases.join(' or ')).join(' · ');
}

// ── Persisted form ──────────────────────────────────────────────────────────

const COND_SEP = '~';
const OPEN_START = '1900-01-01';
const OPEN_END = '2999-12-31';

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Matches how the page has always written list values: only `&` escaped. */
function encodeListValue(value: string): string {
  return String(value ?? '').replace(/&/g, '%26');
}

/** A `cond=` value must survive `&` and `~` splits and the page's substring reads. */
function encodeCondValue(value: string): string {
  return encodeURIComponent(String(value ?? '')).replace(/~/g, '%7E');
}

function shiftDays(isoDate: string, days: number): string {
  const date = stringToDate(isoDate);
  if (!date || isNaN(date.getTime())) return isoDate;
  date.setDate(date.getDate() + days);
  return dateToString(date);
}

function dateRangeOf(condition: InboxFilterCondition): [string, string] {
  switch (condition.op) {
    case 'on': return [condition.value, condition.value];
    case 'before': return [OPEN_START, shiftDays(condition.value, -1)];
    case 'after': return [shiftDays(condition.value, 1), OPEN_END];
    case 'between': return [condition.value, condition.valueTo];
    default: return ['', ''];
  }
}

function amountRangeOf(condition: InboxFilterCondition): [string, string] {
  switch (condition.op) {
    case 'eq': return [condition.value, condition.value];
    case 'gte': return [condition.value, ''];
    case 'lte': return ['', condition.value];
    case 'between': return [condition.value, condition.valueTo];
    default: return ['', ''];
  }
}

/**
 * Conditions → the Inbox's criteria string. The search endpoint takes one date
 * range and one amount range, so a second date (or amount) condition narrows
 * the first rather than being dropped.
 */
export function inboxConditionsToCriteria(conditions: InboxFilterCondition[], description: string = ''): string {
  const usable = expandInboxConditions(conditions).filter(isInboxConditionComplete);

  const lists: { [param: string]: string[] } = {};
  let dateStart = '';
  let dateEnd = '';
  let amountMin = '';
  let amountMax = '';

  usable.forEach(c => {
    const def = inboxFieldDef(c.field);
    if (def?.type === 'enum' && def.param) {
      lists[def.param] = (lists[def.param] || []).concat([c.value]);
    } else if (c.field === 'date') {
      const [start, end] = dateRangeOf(c);
      if (start && (!dateStart || start > dateStart)) dateStart = start;
      if (end && (!dateEnd || end < dateEnd)) dateEnd = end;
    } else if (c.field === 'amount') {
      const [min, max] = amountRangeOf(c);
      if (min !== '' && (amountMin === '' || Number(min) > Number(amountMin))) amountMin = min;
      if (max !== '' && (amountMax === '' || Number(max) < Number(amountMax))) amountMax = max;
    }
  });

  const list = (param: string) => (lists[param]?.length
    ? lists[param].map(v => `${param}=${encodeListValue(v)}`).join('&')
    : `${param}=`);

  const parts = [
    list('suppliers'),
    'dateOption=',
    `dateStart=${dateStart}`,
    `dateEnd=${dateEnd}`,
    list('categories'),
    list('tags'),
    list('track1'),
    list('track2'),
    list('submitters'),
    list('assignedTo'),
    `amountMin=${amountMin}`,
    `amountMax=${amountMax}`
  ];

  usable.forEach(c => {
    parts.push(`cond=${c.field}${COND_SEP}${c.op}${COND_SEP}${encodeCondValue(c.value)}${COND_SEP}${encodeCondValue(c.valueTo)}`);
  });

  if (description?.trim()) {
    parts.push(`desc=${encodeURIComponent(description.trim())}`);
  }

  return `?${parts.join('&')}`;
}

function segmentsOf(criteria: string): string[] {
  return (criteria || '').replace(/^\?/, '').split('&').filter(Boolean);
}

function valuesOf(segments: string[], key: string): string[] {
  return segments
    .filter(s => s.startsWith(`${key}=`))
    .map(s => s.slice(key.length + 1))
    .filter(v => v !== '');
}

export function inboxDescriptionFromCriteria(criteria: string): string {
  const raw = valuesOf(segmentsOf(criteria), 'desc')[0];
  return raw ? safeDecode(raw) : '';
}

/** Drops the panel-only parameters before the page parses a criteria string. */
export function stripPanelParams(criteria: string): string {
  if (!criteria) return criteria;
  const hadLead = criteria.startsWith('?');
  const kept = segmentsOf(criteria).filter(s => !s.startsWith('cond=') && !s.startsWith('desc='));
  return (hadLead ? '?' : '') + kept.join('&');
}

/**
 * Criteria → conditions. Prefers the `cond=` parameters; a filter saved by the
 * old Inbox sidebar only has the flat ones, which read back as one condition
 * per value.
 */
export function inboxCriteriaToConditions(criteria: string): InboxFilterCondition[] {
  if (!criteria) return [];
  const segments = segmentsOf(criteria);

  const modern = valuesOf(segments, 'cond').map(raw => {
    const [field, op, value, valueTo] = raw.split(COND_SEP);
    return {
      field: field as InboxConditionField,
      op,
      value: safeDecode(value || ''),
      valueTo: safeDecode(valueTo || ''),
      confirmed: true
    } as InboxFilterCondition;
  }).filter(c => !!inboxFieldDef(c.field) && inboxOpsFor(c.field).some(o => o.value === c.op));

  if (modern.length) return modern;

  const conditions: InboxFilterCondition[] = [];
  const add = (field: InboxConditionField, op: string, value: string, valueTo?: string) =>
    conditions.push({ field, op, value, valueTo, confirmed: true });

  INBOX_CONDITION_FIELDS
    .filter(f => f.type === 'enum' && f.param)
    .forEach(f => valuesOf(segments, f.param).forEach(v => add(f.value, 'is', safeDecode(v.replace(/%20/g, ' ')))));

  const dateStart = valuesOf(segments, 'dateStart')[0];
  const dateEnd = valuesOf(segments, 'dateEnd')[0];
  if (dateStart && dateEnd) add('date', 'between', dateStart, dateEnd);
  else if (dateStart) add('date', 'after', shiftDays(dateStart, -1));
  else if (dateEnd) add('date', 'before', shiftDays(dateEnd, 1));

  const amountMin = valuesOf(segments, 'amountMin')[0];
  const amountMax = valuesOf(segments, 'amountMax')[0];
  if (amountMin && amountMax) add('amount', 'between', amountMin, amountMax);
  else if (amountMin) add('amount', 'gte', amountMin);
  else if (amountMax) add('amount', 'lte', amountMax);

  return conditions;
}

export function describeInboxCriteria(criteria: string, labels: InboxFieldLabels = {}): string {
  return describeInboxConditions(inboxCriteriaToConditions(criteria), labels);
}

/** The icon a saved filter shows in the picker, from its first condition. */
export function iconForInboxConditions(conditions: InboxFilterCondition[]): string {
  switch ((conditions || [])[0]?.field) {
    case 'state': return 'fa-inbox';
    case 'date': return 'fa-calendar';
    case 'amount': return 'fa-money';
    case 'tag': return 'fa-tag';
    case 'supplier': return 'fa-building-o';
    case 'category': return 'fa-folder-o';
    case 'job':
    case 'job2': return 'fa-briefcase';
    case 'submitter':
    case 'assignee': return 'fa-user-o';
    default: return 'fa-bookmark-o';
  }
}
